import React, { useState } from "react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";

export const DivByAnima = (): JSX.Element => {
  const activities = [
    {
      name: "Creative Play",
      icon: "💡",
      image: "/assets/img1.png",
      description: [
        "Origami Sheets with simple folding lines - make a paper plane, bird, or elephant!",
        "Sticker Sheets themed on travel, festivals, or animals - perfect for trays or notebooks.",
        "Mini Art Contests - Kids can color a themed drawing and submit via QR for rewards.",
        "✨ All designed to be neat, no scissors or glue required.",
      ],
    },
    {
      name: "Indian Tales",
      icon: "📚",
      image: "/assets/img2.png",
      description: [
        "Timeless stories, modern format.",
        "Pocket-sized Story Cards featuring Panchatantra, Tenali Rama, and folk tales.",
        "Bilingual: Available in English + regional language (customizable per route).",
        "Each card includes a 'What did you learn?' prompt for parent-child bonding.",
        "🧡 Helps kids explore values, wit, and Indian culture on the go.",
      ],
    },
    {
      name: "Mini Art Station",
      icon: "🎨",
      image: "/assets/img3.jpg",
      description: [
        "Foldable Coloring Books themed on aviation, trains, or festivals.",
        "Lacing Cards - Fine motor fun with yarn and punch-out shapes.",
        "Sturdy Crayons - 4 color mini pack, non-toxic and break-resistant.",
        "🖍️ No sharp edges. No rolling pencils. All safe and self-contained.",
      ],
    },
    {
      name: "Smart Games",
      icon: "🧠",
      image: "/assets/img4.jpg",
      description: [
        "Festival Bingo - Match festivals with states or icons (great family game!).",
        "Match the Food to the State - Yummy way to learn Indian diversity.",
        "Maze Books & Spot the Difference - Perfect for solo brain time.",
        "🧩 Small cards, huge cognitive boost.",
      ],
    },
    {
      name: "Surprise Element",
      icon: "🎁",
      image: "/assets/img5.jpg",
      description: [
        "DIY Badges (e.g., 'Junior Pilot' or 'Little Explorer')",
        "Stick-On Pilot Wings / Train Hats - Fun role-play, photo-perfect.",
        "Travel Stamp Card - Kids stamp each trip and earn digital badges!",
        "📸 Great for Instagrammable moments parents love to share.",
      ],
    },
    {
      name: "Eco-Friendly Goodies",
      icon: "🌿",
      image: "/assets/img6.jpg",
      description: [
        "100% Paper-Based Materials - No plastic toys or wraps.",
        "Kraft Paper Folder or Pouch - Light, recyclable, and Airline/train approved.",
        "Vegetable ink prints, safe for kids and nature.",
        "🌱 Travel light. Tread light.",
      ],
    },
  ];

  const [selectedActivity, setSelectedActivity] = useState<string | null>("Creative Play");

  return (
    <section
      id="activities"
      className="flex flex-col w-full items-start gap-8 md:gap-16 p-4 md:p-16 bg-white"
    >
      <div className="flex flex-col items-center gap-6 md:gap-8 w-full">
        <div className="inline-flex items-center border text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground px-4 py-2 rounded-full border-[#169c79] opacity-90">
          <span className="font-extrabold text-[#169c79] text-xs leading-[16.8px]">ACTIVITIES</span>
        </div>

        <div className="flex flex-col items-center justify-center gap-4 w-full">
          <h2 className="font-bold text-[#1a1a1a] text-[5vw] md:text-[3vw] leading-tight md:leading-[60px] text-center">
            What's Inside the Monster Kit?
          </h2>
          <p className="text-[#595959] text-[4vw] md:text-base text-center leading-6">
            Six categories of screen-free activities designed to engage curious minds and keep little hands busy during travel adventures.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 md:gap-4 justify-center w-full">
        {activities.map((activity, index) => (
          <Button
            key={index}
            onClick={() => setSelectedActivity(activity.name)}
            variant={selectedActivity === activity.name ? "default" : "outline"}
            className={`justify-center whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 flex items-center gap-2 px-4 py-2 rounded-full border border-[#003230] md:min-w-[150px] ${
              selectedActivity === activity.name
                ? "bg-[#003230] text-white"
                : "bg-white text-[#003230]"
            }`}
          >
            {activity.icon}
            {activity.name}
          </Button>
        ))}
      </div>

      {selectedActivity && (
        <Card className="border bg-card text-card-foreground shadow flex flex-col md:flex-row items-start gap-6 md:gap-8 p-4 md:p-8 w-full rounded-3xl border-[#f2edfb]">
          <CardContent className="p-0 flex flex-col md:flex-row w-full gap-6 md:gap-8">
            <div className="flex-shrink-0 w-full md:w-[40%] h-[40vw] md:h-[300px]">
              <img
                src={activities.find((activity) => activity.name === selectedActivity)?.image || ""}
                alt={selectedActivity}
                className="object-cover w-full h-full rounded-2xl"
              />
            </div>

            <div className="flex-1 flex flex-col items-start">
              <h3 className="text-[#169c79] text-[5vw] md:text-[3vw] font-extrabold leading-tight md:leading-[60px]">
                {activities.find((activity) => activity.name === selectedActivity)?.name}
              </h3>

              <div className="text-[#595959] text-[5vw] md:text-base leading-6">
                {activities
                  .find((activity) => activity.name === selectedActivity)
                  ?.description.map((point, idx) => (
                    <div key={idx} className="my-2 flex items-start">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="mr-2"
                      >
                        <path
                          d="M12 24C10.374 24 8.84 23.286 7.792 22.041C6.252 22.217 4.665 21.636 3.515 20.486C2.366 19.336 1.786 17.746 1.925 16.124C0.714 15.16 0 13.626 0 12C0 10.374 0.714 8.84 1.96 7.792C1.785 6.255 2.365 4.665 3.515 3.515C4.665 2.365 6.252 1.782 7.876 1.925C8.84 0.715 10.374 0 12 0C13.626 0 15.16 0.714 16.208 1.959C17.75 1.786 19.335 2.364 20.485 3.514C21.634 4.664 22.214 6.254 22.075 7.876C23.286 8.84 24 10.374 24 12C24 13.626 23.286 15.16 22.04 16.208C22.215 17.745 21.635 19.335 20.485 20.485C19.334 21.635 17.744 22.211 16.124 22.075C15.16 23.285 13.626 24 12 24ZM7.873 20.076C8.434 20.076 8.954 20.317 9.321 20.752C9.989 21.545 10.965 22 12 22C13.035 22 14.011 21.545 14.679 20.752C15.082 20.273 15.669 20.031 16.295 20.082C17.329 20.169 18.339 19.802 19.071 19.07C19.802 18.339 20.171 17.327 20.083 16.294C20.029 15.67 20.273 15.081 20.753 14.677C21.545 14.01 22 13.033 22 11.999C22 10.965 21.545 9.988 20.753 9.321C20.274 8.918 20.029 8.328 20.083 7.704C20.171 6.671 19.803 5.659 19.071 4.928C18.339 4.197 17.323 3.834 16.296 3.916C15.67 3.972 15.082 3.725 14.679 3.247C14.011 2.454 13.035 1.999 12 1.999C10.965 1.999 9.989 2.454 9.321 3.247C8.917 3.726 8.328 3.966 7.705 3.917C6.666 3.827 5.661 4.197 4.929 4.929C4.198 5.66 3.829 6.672 3.917 7.705C3.971 8.329 3.727 8.918 3.247 9.322C2.455 9.989 2 10.966 2 12C2 13.034 2.455 14.011 3.247 14.678C3.726 15.081 3.971 15.671 3.917 16.295C3.829 17.328 4.197 18.34 4.929 19.071C5.661 19.802 6.682 20.166 7.704 20.083C7.761 20.078 7.817 20.076 7.873 20.076ZM12.801 15.135L17.54 10.567C17.937 10.184 17.949 9.55 17.565 9.153C17.182 8.756 16.549 8.744 16.151 9.127L11.399 13.708C11.008 14.099 10.377 14.099 9.959 13.683L7.681 11.566C7.279 11.191 6.645 11.213 6.268 11.618C5.892 12.022 5.915 12.655 6.32 13.031L8.572 15.123C9.158 15.709 9.929 16.002 10.698 16.002C11.463 16.002 12.224 15.713 12.802 15.136L12.801 15.135Z"
                          fill="#003230"
                        />
                      </svg>
                      <p>{point}</p>
                    </div>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  );
};
